/**
 * CJ · Store locator / Boutique OS 5.1.3
 * One isolated component, progressively enhanced from a server-rendered list.
 * No visitor geolocation. A location selection can initiate the requested Google map.
 * Maps JS credentials belong to the merchant and must be HTTP-referrer/API restricted.
 */
let googleLoader;
const clean = (text = '') => String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const slug = (text) => clean(text).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const positionOf = (location) => {
  if (!location?.lat?.trim() || !location?.lng?.trim()) return null;
  const lat = Number(location.lat), lng = Number(location.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
};
const safeExternal = (value) => {
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : ''; } catch { return ''; }
};
const safeEmbed = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && ['www.google.com', 'maps.google.com'].includes(url.hostname) && (url.pathname === '/maps/embed' || url.pathname.startsWith('/maps/embed/')) ? url.href : '';
  } catch { return ''; }
};
const withTimeout = (promise, ms = 15000) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('Google Maps no respondió a tiempo.')), ms);
  promise.then((value) => { clearTimeout(timer); resolve(value); }, (error) => { clearTimeout(timer); reject(error); });
});
function loadGoogle(key) {
  if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps);
  if (googleLoader) return googleLoader;
  // Respect an existing Maps loader: do not add a second copy from another app/section.
  if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
    googleLoader = new Promise((resolve, reject) => {
      let elapsed = 0;
      const timer = setInterval(() => {
        if (window.google?.maps?.importLibrary) { clearInterval(timer); resolve(window.google.maps); }
        else if ((elapsed += 100) >= 12000) { clearInterval(timer); reject(new Error('No se pudo usar el mapa existente.')); }
      }, 100);
    });
    return googleLoader;
  }
  googleLoader = new Promise((resolve, reject) => {
    const callbackName = '__cjLocatorMapsReady';
    const script = document.createElement('script');
    const previousAuthFailure = window.gm_authFailure;
    let finished = false;
    const finish = (error) => {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      if (error) reject(error); else resolve(window.google.maps);
    };
    window[callbackName] = () => finish();
    window.gm_authFailure = () => {
      finish(new Error('Google rechazó la configuración del mapa.'));
      window.dispatchEvent(new Event('cj:maps-auth-failure'));
      if (typeof previousAuthFailure === 'function') previousAuthFailure();
    };
    const timeout = setTimeout(() => finish(new Error('Google Maps no respondió a tiempo.')), 15000);
    const params = new URLSearchParams({ key, loading: 'async', callback: callbackName, v: 'weekly', language: 'es', region: 'CO', auth_referrer_policy: 'origin' });
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    const nonce = document.querySelector('script[nonce]')?.nonce;
    if (nonce) script.nonce = nonce;
    script.onerror = () => finish(new Error('No se pudo conectar con Google Maps.'));
    document.head.append(script);
  });
  return googleLoader;
}

class CJStoreLocator extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this._abort = new AbortController();
    this._markers = new Map();
    this._mapReady = false;
    this._consent = false;
    this._mapFailed = false;
    this._loading = false;
    this._embedURL = '';
    this._embedFailedURL = '';
    this._requestSerial = (this._requestSerial || 0) + 1;
    this._q = (selector) => this.querySelector(selector);
    this._city = this._q('[data-city-filter]');
    this._search = this._q('[data-location-search]');
    this._list = this._q('[data-list-panel]');
    this._detail = this._q('[data-selected-detail]');
    if (!this._city || !this._search || !this._list || !this._detail) { this._connected = false; return; }
    this._locations = [...this.querySelectorAll('[data-location]')].map((element, index) => ({
      ...element.dataset, element, item: element.closest('[data-location-item]'), index,
      source: element.querySelector('[data-facts-source]'), facts: element.querySelector('[data-location-facts]'),
      trigger: element.querySelector('[data-select-location]'),
      text: clean([element.dataset.title, element.dataset.city, element.dataset.country, element.dataset.address].join(' '))
    }));
    const signal = this._abort.signal;
    // Image failures never remove a service label, a location name or its link.
    const hideBrokenImage = (image) => {
      if (!image.matches?.('.cj-loc__thumb-img,.cj-loc__service-icon,.cj-loc__venue-image')) return;
      image.hidden = true;
      if (image.matches('.cj-loc__venue-image')) {
        const frame = image.closest('[data-location-photo-frame]');
        if (frame) frame.hidden = true;
        image.closest('[data-location-experience]')?.classList.add('cj-loc__experience--no-photo');
      }
    };
    this.addEventListener('error', (event) => hideBrokenImage(event.target), { capture: true, signal });
    this.querySelectorAll('.cj-loc__thumb-img,.cj-loc__service-icon,.cj-loc__venue-image').forEach((image) => {
      if (image.complete && !image.naturalWidth) hideBrokenImage(image);
    });
    this._city.addEventListener('change', () => this._filter(true), { signal });
    this._search.addEventListener('input', () => this._filter(true), { signal });
    this.addEventListener('click', (event) => {
      const selection = event.target.closest('[data-select-location]');
      if (selection && this.contains(selection)) {
        // Keep native modified-click navigation and a useful no-JS anchor.
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const location = this._locations.find((entry) => entry.trigger === selection);
        if (location) {
          this._select(location, { updateURL: true, activateMap: true });
          // Mobile scrolls to the shared map; the full venue list remains visible below it.
          if (!this._media.matches) this._setView('map', true);
        }
        return;
      }
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-reset')) {
        this._city.value = ''; this._search.value = ''; this._filter(true); this._setView('list'); this._city.focus();
      } else if (target.hasAttribute('data-view')) {
        this._setView(target.dataset.view, true);
        if (target.dataset.view === 'map') void this._loadMap();
      } else if (target.hasAttribute('data-load-map')) {
        void this._loadMap();
      } else if (target.hasAttribute('data-fit')) {
        this._drawMap(true);
      } else if (target.hasAttribute('data-selected-details')) {
        this._setView('list'); this._revealSelected(true);
      }
    }, { signal });
    window.addEventListener('hashchange', () => this._restoreHash(), { signal });
    window.addEventListener('cj:maps-auth-failure', () => this._mapFailure(), { signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      const location = this._locations.find((entry) => entry.id === event.detail?.blockId || entry.element.contains(event.target));
      if (!location) return;
      this._city.value = ''; this._search.value = ''; this._filter(false);
      this._select(location); this._setView('list'); this._revealSelected(false);
    }, { signal });
    this._media = window.matchMedia('(min-width:990px)');
    this._media.addEventListener('change', () => {
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      const owner = this._visible.find((entry) => entry.facts?.contains(focused));
      // If a visible mobile fact had focus, keep that same venue active when it moves right.
      if (this._media.matches && owner) this._select(owner);
      else this._placeFacts();
      if (focused?.isConnected) focused.focus({ preventScroll: true });
      if (this._media.matches && this.dataset.desktopMapOpen !== 'false') void this._loadMap();
      else if (this._mapReady) requestAnimationFrame(() => this._refreshMap());
    }, { signal });
    this._city.value = slug(this.dataset.defaultCity || '');
    this._selected = null;
    this.querySelectorAll('[data-controls],[data-view-controls],[data-reset]').forEach((element) => { element.hidden = false; });
    this.dataset.ready = '';
    this._setView('list');
    this._filter(false);
    this._restoreHash();
    if (this.dataset.requestMap === 'false' || (this._media.matches && this.dataset.desktopMapOpen !== 'false')) void this._loadMap();
  }

  disconnectedCallback() {
    this._connected = false;
    this._abort?.abort();
    this._requestSerial++;
    clearTimeout(this._embedTimer); clearTimeout(this._embedDelay);
    this._markers?.forEach(({ marker }) => { window.google?.maps?.event?.clearInstanceListeners(marker); marker.map = null; });
    this._markers?.clear();
    if (this._map) window.google?.maps?.event?.clearInstanceListeners(this._map);
    this._map = null; this._mapReady = false;
    this._q?.('[data-map-canvas]')?.replaceChildren();
    this._q?.('[data-embed-host]')?.replaceChildren();
    this._locations?.forEach((entry) => { if (entry.facts && entry.source) entry.source.append(entry.facts); });
    this._embedURL = ''; this._pendingEmbedURL = ''; this._loading = false;
    delete this.dataset.ready;
  }

  _mapConfigured() { return this.dataset.mapMode === 'multiple' && Boolean(this.dataset.apiKey?.trim() && this.dataset.mapId?.trim()); }

  _embedSpec(location = this._selected) {
    if (!location) return null;
    const saved = safeEmbed(location.embedUrl);
    if (saved) return { url: saved, note: 'Mapa insertado de esta sede.' };
    const point = positionOf(location);
    if (!point && !location.address?.trim()) return null;
    const query = point ? `${point.lat},${point.lng}` : [location.address, location.city, location.country].filter(Boolean).join(', ');
    if (!query.trim()) return null;
    const zoom = String(Math.max(10, Math.min(20, Number(this.dataset.mapZoom) || 16)));
    const key = this.dataset.embedKey?.trim();
    let url;
    if (key) {
      url = new URL('https://www.google.com/maps/embed/v1/place');
      url.search = new URLSearchParams({ key, q: query, zoom, language: 'es' }).toString();
    } else if (this.dataset.queryEmbed !== 'false') {
      // Compatibility viewer, NOT the official Maps Embed API and not a multipin map.
      // An explicit Share -> Embed URL takes priority. Google controls the public viewer.
      url = new URL('https://maps.google.com/maps');
      url.search = new URLSearchParams({ q: query, z: zoom, hl: 'es', output: 'embed' }).toString();
    } else return null;
    return { url: url.href, note: point ? (location.mapNote || 'Punto según las coordenadas guardadas.') : 'Ubicación consultada por dirección. Confirma el punto en Google Maps.' };
  }

  _filter(updateURL) {
    const words = clean(this._search.value).split(/\s+/).filter(Boolean);
    const city = this._city.value;
    this._visible = this._locations.filter((location) => {
      const matches = (!city || location.citySlug === city) && words.every((word) => location.text.includes(word));
      location.item.hidden = !matches;
      return matches;
    });
    const count = this._visible.length;
    this._q('[data-result-count]').textContent = `${count} ${count === 1 ? 'sede encontrada' : 'sedes encontradas'}${city ? ` en ${this._city.selectedOptions[0].textContent}` : ''}`;
    this._q('[data-empty]').hidden = count > 0;
    const next = this._visible.includes(this._selected) ? this._selected : this._visible[0] || null;
    this._select(next, { updateURL });
    this.querySelector('[data-view="map"]')?.toggleAttribute('disabled', !count);
  }

  _select(location, { updateURL = false, reveal = false, activateMap = false } = {}) {
    const changed = this._selected !== location;
    this._selected = location;
    this._placeFacts();
    if (changed && this._media.matches) this._detail.scrollTop = 0;
    this._locations.forEach((entry) => {
      entry.element.toggleAttribute('data-selected', entry === location);
      if (entry === location) entry.trigger?.setAttribute('aria-current', 'true'); else entry.trigger?.removeAttribute('aria-current');
    });
    this._q('[data-active-city]').textContent = location?.city || '';
    this._q('[data-active-title]').textContent = location?.title || 'Encuentra tu sede';
    const photo = this._q('.cj-loc__cover-photo');
    if (photo) {
      const src = location?.photo;
      if (src) {
        if (photo.getAttribute('src') !== src) { photo.removeAttribute('srcset'); photo.src = src; }
        photo.alt = location.title; photo.hidden = false;
        photo.onerror = () => { photo.hidden = true; };
      } else { photo.hidden = true; photo.removeAttribute('src'); }
    }
    const url = safeExternal(location?.mapUrl || '');
    this.querySelectorAll('[data-external-map],[data-map-link]').forEach((external) => {
      external.hidden = !url;
      if (url) external.href = url; else external.removeAttribute('href');
    });
    this._q('[data-selected-details]').hidden = !location;
    if (changed || activateMap) this._embedFailedURL = '';
    if (activateMap) this._consent = true;
    this._refreshMap();
    if (activateMap && this._mapConfigured() && !this._mapReady && !this._mapFailed) void this._loadMap();
    if (updateURL) {
      const hash = location ? `#sede-${location.id}` : this._city.value ? `#${this._city.value}` : '#sedes';
      try { history.replaceState(history.state, '', `${locationURL()}${hash}`); } catch { /* Sandboxed Theme Editor may deny URL updates. */ }
    }
    if (reveal) this._revealSelected(false);
  }

  _restoreHash() {
    let hash;
    try { hash = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
    if (!hash) return;
    const location = this._locations.find((entry) => `sede-${entry.id}` === hash);
    if (location) {
      this._city.value = location.citySlug; this._search.value = ''; this._filter(false); this._select(location, { reveal: true }); return;
    }
    const city = [...this._city.options].find((option) => option.value && option.value === slug(hash));
    if (city) { this._city.value = city.value; this._search.value = ''; this._filter(false); }
  }

  _placeFacts() {
    // Never clone content or create multiple service lists / maps. CSS and this breakpoint agree.
    this._locations.forEach((entry) => {
      const destination = this._media.matches && entry === this._selected ? this._detail : entry.source;
      if (entry.facts && destination && entry.facts.parentElement !== destination) destination.append(entry.facts);
    });
  }

  _setView(view, focus = false) {
    // These are jump links now, not tabs. Mobile always displays map + ALL matching full cards.
    delete this.dataset.view;
    this.querySelectorAll('[data-view]').forEach((button) => button.removeAttribute('aria-pressed'));
    if (view === 'map' && this._mapReady) requestAnimationFrame(() => this._refreshMap());
    if (!focus) return;
    if (view === 'map') {
      const heading = this._q('[data-active-title]');
      heading.tabIndex = -1; heading.focus({ preventScroll: true });
      if (!this._media.matches) this._q('[data-map-panel]').scrollIntoView({ block: 'start', behavior: 'instant' });
    } else {
      this._list.scrollIntoView({ block: 'start', behavior: 'instant' });
      const target = this._visible[0]?.trigger || this._list;
      target.focus({ preventScroll: true });
    }
  }

  _revealSelected(focus) {
    const element = this._selected?.element;
    if (!element) return;
    if (this._media.matches) {
      const entry = element.getBoundingClientRect(), list = this._list.getBoundingClientRect();
      this._list.scrollTop += entry.top - list.top - 6;
    } else element.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    if (focus) this._selected.trigger?.focus({ preventScroll: true });
  }

  _refreshMap() {
    if (this._mapReady && !this._mapFailed && positionOf(this._selected)) { this._drawMap(false); return; }
    const spec = this._embedSpec();
    if (this._consent && spec && spec.url !== this._embedFailedURL && (!this._mapConfigured() || this._mapFailed || this._mapReady)) {
      this._showEmbed(spec); return;
    }
    clearTimeout(this._embedDelay); this._pendingEmbedURL = '';
    clearTimeout(this._embedTimer);
    this._q('[data-embed-host]').replaceChildren(); this._embedURL = '';
    this._q('[data-map-loading]').hidden = true;
    this._q('[data-map-cover]').hidden = false;
    this._q('[data-map-canvas]').hidden = true;
    this._q('[data-embed-host]').hidden = true;
    this._q('[data-fit]').hidden = true;
    const canLoad = Boolean(this._selected && (spec || this._mapConfigured()));
    const loadButton = this._q('[data-load-map]');
    loadButton.hidden = !canLoad;
    loadButton.disabled = Boolean(this._loading);
    loadButton.textContent = this._loading ? 'Cargando mapa…' : this._embedFailedURL ? 'Reintentar mapa' : 'Cargar mapa de Google';
    this._q('[data-map-message]').textContent = !this._selected ? 'Elige otra ciudad o limpia los filtros para encontrar una sede.' :
      this._embedFailedURL || this._mapFailed ? 'El mapa no pudo cargarse. Puedes reintentar o abrir esta sede en Google Maps.' :
      canLoad ? 'Selecciona una tarjeta o carga el mapa para ubicar esta sede en Google Maps.' : 'Configura el mapa insertado de esta sede. Su enlace de Google Maps sigue disponible.';
    this._q('[data-map-caption]').textContent = this._selected ? 'El mapa se conecta con Google; sus ubicaciones deben verificarse antes de la visita.' : 'Sin sedes para mostrar.';
  }

  async _loadMap() {
    if (!this._selected) return;
    this._consent = true;
    this._embedFailedURL = '';
    if (!this._mapConfigured() || this._mapFailed) { this._refreshMap(); return; }
    if (this._mapReady) { this._refreshMap(); return; }
    if (this._loading) return;
    this._loading = true;
    const serial = this._requestSerial;
    const button = this._q('[data-load-map]');
    button.disabled = true; button.textContent = 'Cargando mapa…';
    try {
      const maps = await loadGoogle(this.dataset.apiKey.trim());
      const [{ Map: GoogleMap }, { AdvancedMarkerElement }] = await withTimeout(Promise.all([maps.importLibrary('maps'), maps.importLibrary('marker')]));
      if (!this._connected || serial !== this._requestSerial) return;
      const initial = positionOf(this._selected) ? this._selected : this._visible.find(positionOf) || this._locations.find(positionOf);
      if (!initial) throw new Error('No hay puntos configurados.');
      const canvas = this._q('[data-map-canvas]');
      canvas.hidden = false;
      this._map = new GoogleMap(canvas, {
        center: positionOf(initial), zoom: Number(this.dataset.mapZoom) || 16, mapId: this.dataset.mapId.trim(),
        streetViewControl: false, mapTypeControl: false, fullscreenControl: true,
        gestureHandling: 'cooperative', clickableIcons: false, zoomControl: true
      });
      this._locations.forEach((location) => {
        const position = positionOf(location);
        if (!position) return;
        const pin = document.createElement('div'); pin.className = 'cj-loc-marker';
        const number = document.createElement('span'); number.textContent = String(location.index + 1); pin.append(number);
        const marker = new AdvancedMarkerElement({ map: this._map, position, title: `${location.title}, ${location.city}`, content: pin, gmpClickable: true });
        marker.addEventListener('gmp-click', () => this._select(location, { updateURL: true, reveal: true, activateMap: true }), { signal: this._abort.signal });
        this._markers.set(location.id, { marker, pin });
      });
      this._mapReady = true; this._mapFailed = false;
      this._refreshMap();
    } catch {
      if (this._connected && serial === this._requestSerial) this._mapFailure();
    } finally {
      if (this._connected && serial === this._requestSerial) { this._loading = false; button.disabled = false; button.textContent = 'Cargar mapa de Google'; }
    }
  }

  _mapFailure() { this._mapFailed = true; if (this._connected) this._refreshMap(); }

  _drawMap(fit) {
    if (!this._mapReady || this._mapFailed || !this._selected) return;
    const visibleIds = new Set(this._visible.map((entry) => entry.id));
    const pins = this._visible.filter(positionOf);
    this._markers.forEach(({ marker, pin }, id) => {
      marker.map = visibleIds.has(id) ? this._map : null;
      pin.toggleAttribute('data-active', this._selected?.id === id);
      marker.zIndex = this._selected?.id === id ? 1000 : 1;
    });
    const current = positionOf(this._selected);
    if (!pins.length || (!current && !fit)) { this._refreshMap(); return; }
    clearTimeout(this._embedDelay); clearTimeout(this._embedTimer);
    this._pendingEmbedURL = ''; this._embedURL = '';
    this._q('[data-embed-host]').replaceChildren(); this._q('[data-embed-host]').hidden = true;
    this._q('[data-map-loading]').hidden = true;
    this._q('[data-map-canvas]').hidden = false;
    this._q('[data-map-cover]').hidden = true;
    this._q('[data-fit]').hidden = false;
    this._q('[data-load-map]').hidden = true;
    const coverage = `${pins.length} de ${this._visible.length} sedes tienen punto configurado.`;
    this._q('[data-map-caption]').textContent = fit ? coverage : `${this._selected.mapNote || 'Punto según las coordenadas guardadas.'} ${coverage}`;
    if (fit) {
      if (pins.length === 1) { this._map.setCenter(positionOf(pins[0])); this._map.setZoom(Number(this.dataset.mapZoom) || 16); }
      else {
        const bounds = new window.google.maps.LatLngBounds();
        pins.forEach((location) => bounds.extend(positionOf(location)));
        this._map.fitBounds(bounds, 52);
      }
    } else { this._map.panTo(current); this._map.setZoom(Number(this.dataset.mapZoom) || 16); }
  }

  _showEmbed(spec) {
    if (!this._consent || !this._selected) return;
    const host = this._q('[data-embed-host]');
    this._q('[data-map-canvas]').hidden = true;
    this._q('[data-map-cover]').hidden = true;
    this._q('[data-fit]').hidden = true;
    host.hidden = false;
    this._q('[data-map-caption]').textContent = spec.note;
    if (this._embedURL === spec.url && host.firstElementChild) {
      host.firstElementChild.title = `Google Maps: ${this._selected.title}, ${this._selected.city}`;
      return;
    }
    if (this._pendingEmbedURL === spec.url && this._pendingEmbedLocation === this._selected.id) return;
    // Do not display the previous city's map while the new selection is pending.
    clearTimeout(this._embedTimer); clearTimeout(this._embedDelay);
    host.replaceChildren(); this._embedURL = '';
    this._pendingEmbedURL = spec.url;
    this._pendingEmbedLocation = this._selected.id;
    this._q('[data-map-loading]').hidden = false;
    const serial = this._requestSerial;
    const locationId = this._selected.id;
    this._embedDelay = setTimeout(() => {
      if (!this._connected || serial !== this._requestSerial || this._selected?.id !== locationId) return;
      this._pendingEmbedURL = '';
      const frame = document.createElement('iframe');
      frame.title = `Google Maps: ${this._selected.title}, ${this._selected.city}`;
      frame.referrerPolicy = 'strict-origin-when-cross-origin'; frame.allowFullscreen = true;
      const current = () => this._connected && serial === this._requestSerial && host.firstElementChild === frame;
      const fail = () => {
        if (!current()) return;
        clearTimeout(this._embedTimer);
        this._embedFailedURL = spec.url;
        this._refreshMap();
      };
      frame.addEventListener('load', () => {
        if (!current()) return;
        clearTimeout(this._embedTimer); this._q('[data-map-loading]').hidden = true;
        // Cross-origin load is not certification that the pin/Google UI is correct.
      }, { once: true });
      frame.addEventListener('error', fail, { once: true });
      frame.src = spec.url;
      host.replaceChildren(frame); this._embedURL = spec.url;
      this._embedTimer = setTimeout(fail, 20000);
    }, 160);
  }
}
function locationURL() { return window.location.pathname + window.location.search; }
if (!customElements.get('cj-store-locator')) customElements.define('cj-store-locator', CJStoreLocator);
